import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { clearBreakSession } from '../utils/breakSessionStorage';
import {
  DEMO_PREVIEW_STORAGE_KEY,
  getAuthAccessToken,
  isDemoPreviewTab,
  clearDemoPreviewTabSession,
  clearDemoPreviewAuthAndBackup,
} from '../utils/demoPreviewStorage';
import {
  isMarketingPreviewTab,
  clearMarketingPreviewTabSession,
} from '../utils/marketingPreviewStorage';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);

  // Check if user is authenticated on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = getAuthAccessToken();
      if (token) {
        try {
          const response = await api.get('/auth/profile', { skipAuthRedirect: true });
          setUser(response.data.data);
          setPermissions(response.data.data.permissions || []);
        } catch (error) {
          console.error('Auth init error:', error);
          if (isMarketingPreviewTab()) {
            clearMarketingPreviewTabSession();
          } else if (isDemoPreviewTab()) {
            clearDemoPreviewAuthAndBackup();
          } else {
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
          }
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // Login step 1 - credentials
  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  };

  // Login step 2 - verify OTP
  const verifyOTP = async (userId, otp, otpType) => {
    const response = await api.post('/auth/verify-otp', { userId, otp, otpType });
    if (response.data.success) {
      const { user, token, refreshToken, punchInMessage } = response.data.data;
      clearMarketingPreviewTabSession();
      clearDemoPreviewTabSession();
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', refreshToken);
      // Store punch-in message to display on dashboard
      if (punchInMessage) {
        sessionStorage.setItem('punchInMessage', punchInMessage);
      }
      setUser(user);
      setPermissions(user.permissions || []);
    }
    return response.data;
  };

  // Resend OTP
  const resendOTP = async (userId) => {
    const response = await api.post('/auth/resend-otp', { userId });
    return response.data;
  };

  // Logout
  const logout = useCallback(() => {
    if (isMarketingPreviewTab()) {
      clearMarketingPreviewTabSession();
      clearBreakSession();
      setUser(null);
      setPermissions([]);
      return;
    }
    if (isDemoPreviewTab()) {
      clearDemoPreviewTabSession();
      try {
        sessionStorage.removeItem(DEMO_PREVIEW_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      clearBreakSession();
      setUser(null);
      setPermissions([]);
      return;
    }
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    try {
      sessionStorage.removeItem(DEMO_PREVIEW_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    clearBreakSession();
    setUser(null);
    setPermissions([]);
  }, []);

  // Check if user has permission
  const hasPermission = useCallback((requiredPermission) => {
    if (!user) return false;
    if (user.is_admin) return true;
    
    if (Array.isArray(requiredPermission)) {
      return requiredPermission.some(p => permissions.includes(p));
    }
    return permissions.includes(requiredPermission);
  }, [user, permissions]);

  // Check if user has module access
  const hasModuleAccess = useCallback((module) => {
    if (!user) return false;
    if (user.is_admin) return true;
    return permissions.some(p => p.startsWith(`${module}_`));
  }, [user, permissions]);

  /** True if user has any of the given modules (e.g. bench_sales OR contacts). */
  const hasAnyModuleAccess = useCallback(
    (modules) => {
      if (!user) return false;
      if (user.is_admin) return true;
      return modules.some((mod) => permissions.some((p) => p.startsWith(`${mod}_`)));
    },
    [user, permissions]
  );

  // Update profile
  const updateProfile = async (data) => {
    const response = await api.put('/auth/profile', data);
    if (response.data.success) {
      setUser(response.data.data);
    }
    return response.data;
  };

  // Change password
  const changePassword = async (currentPassword, newPassword) => {
    const response = await api.post('/auth/change-password', {
      currentPassword,
      newPassword
    });
    return response.data;
  };

  // Refresh profile
  const refreshProfile = async () => {
    try {
      const response = await api.get('/auth/profile', { skipAuthRedirect: true });
      setUser(response.data.data);
      setPermissions(response.data.data.permissions || []);
    } catch (error) {
      console.error('Refresh profile error:', error);
    }
  };

  // Setup 2FA (authenticator app)
  const setup2FA = async (method = 'google_authenticator') => {
    const response = await api.post('/auth/2fa/setup', { method });
    return response.data;
  };

  // Verify 2FA
  const verify2FA = async (otp) => {
    const response = await api.post('/auth/2fa/verify', { otp });
    if (response.data.success) {
      await refreshProfile();
    }
    return response.data;
  };

  // Disable 2FA (org policy: MFA required — API rejects)
  const disable2FA = async (password, otp) => {
    const response = await api.post('/auth/2fa/disable', { password, otp });
    if (response.data.success) {
      await refreshProfile();
    }
    return response.data;
  };

  const enableEmailMfa = async () => {
    const response = await api.post('/auth/2fa/enable-email');
    if (response.data.success) {
      await refreshProfile();
    }
    return response.data;
  };

  const setupPhoneMfa = async (method) => {
    const response = await api.post('/auth/2fa/setup-phone', { method });
    return response.data;
  };

  const verifyPhoneMfa = async (otp) => {
    const response = await api.post('/auth/2fa/verify-phone', { otp });
    if (response.data.success) {
      await refreshProfile();
    }
    return response.data;
  };

  const value = {
    user,
    loading,
    permissions,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || false,
    login,
    verifyOTP,
    resendOTP,
    logout,
    hasPermission,
    hasModuleAccess,
    hasAnyModuleAccess,
    updateProfile,
    changePassword,
    setup2FA,
    verify2FA,
    disable2FA,
    enableEmailMfa,
    setupPhoneMfa,
    verifyPhoneMfa,
    refreshProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
